import React from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './auth/AuthProvider';
import PrivateRoute from './components/PrivateRoute';
import AdminLayout from './components/admin/AdminLayout';

// Client pages
import DashboardPage from './pages/Dashboard';
import OrdersPage from './pages/Orders';
import FinancePage from './pages/Finance';
import SettingsPage from './pages/Settings';
import LoginPage from './pages/Login';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminOrders from './pages/admin/Orders';
import AdminAnnouncements from './pages/admin/Announcements';
import AdminUsers from './pages/admin/Users';
import AdminInvites from './pages/admin/Invites';
import AdminCouriers from './pages/admin/Couriers';
import AdminFinance from './pages/admin/Finance';
import AdminAudit from './pages/admin/Audit';

function Header() {
  const { t } = useTranslation();
  const { perms } = useAuth();
  const { pathname } = useLocation();

  const isAdminRoute = pathname.startsWith('/admin');
  const showAdminLink = (perms ?? []).includes('admin.read');

  if (isAdminRoute) return null;

  return (
    <header className="bg-blue-600 text-white p-4 shadow">
      <div className="container mx-auto flex justify-between items-center">
        <h1 className="text-xl font-semibold">LabelAgent</h1>
        <nav className="space-x-4">
          <Link to="/" className="hover:underline">{t('dashboard')}</Link>
          <Link to="/orders" className="hover:underline">{t('orders')}</Link>
          <Link to="/finance" className="hover:underline">{t('finance')}</Link>
          <Link to="/settings" className="hover:underline">{t('settings')}</Link>
          {showAdminLink && <Link to="/admin" className="hover:underline">Admin</Link>}
        </nav>
      </div>
    </header>
  );
}

export default function App() {
  return (
    <>
      <Header />
      <main className="flex-1 container mx-auto p-4">
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Client (protected) */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <OrdersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <PrivateRoute>
                <FinancePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />

          {/* Admin area */}
          <Route
            path="/admin/*"
            element={
              <PrivateRoute allowedPerms={['admin.read']}>
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="invites" element={<AdminInvites />} />
            <Route path="couriers" element={<AdminCouriers />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<div className="p-8">Not found</div>} />
        </Routes>
      </main>
    </>
  );
}
